import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { ApiError } from '@/lib/api/http-client'
import { persistPortalSession } from '@/lib/auth/portal-session'
import { portalPasswordResetRoutePath } from '@/lib/auth/portal-routes'
import { hasPortalSession } from '@/lib/auth/portal-routes'
import {
  appendUserLoginResumeMarker,
  persistUserPostLoginReturnPath,
  readStoredPortalAccount,
  readUserPostLoginReturnPath,
} from '@/lib/auth/session-storage'
import {
  confirmWecomAuthBinding,
  exchangeWecomAuthSession,
  getWecomAuthContext,
  resolveWecomAuthDestination,
  startWecomEmailBinding,
} from './api'

/** 企微两阶段授权共享逻辑：自动匹配失败后的人工兜底使用当前登录账号，失败保留无关 session。 */
export function useWecomAuthPage() {
  const attempted = useRef(false)
  const context = useQuery({
    queryKey: ['wecom-auth-context'],
    queryFn: getWecomAuthContext,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const clearResume = () => {
    if (readUserPostLoginReturnPath() === '/signin/wecom') persistUserPostLoginReturnPath(null)
  }
  const exchange = useMutation({
    mutationFn: exchangeWecomAuthSession,
    onSuccess: (data) => {
      persistPortalSession('user', data)
      clearResume()
      if (data.password_reset_required) {
        persistUserPostLoginReturnPath(resolveWecomAuthDestination(data.return_to))
      }
      window.location.replace(
        data.password_reset_required
          ? portalPasswordResetRoutePath.user
          : resolveWecomAuthDestination(data.return_to),
      )
    },
    onError: () => {
      void context.refetch()
    },
  })
  const authorize = useMutation({
    mutationFn: () => {
      const version = context.data?.transaction_version
      if (!version) return Promise.reject(new Error('WeCom context unavailable'))
      return startWecomEmailBinding(version)
    },
    onSuccess: (data) => window.location.assign(data.authorization_url),
    onError: () => {
      void context.refetch()
    },
  })
  const confirm = useMutation({
    mutationFn: confirmWecomAuthBinding,
    onSuccess: (data) => {
      clearResume()
      window.location.replace(resolveWecomAuthDestination(data.return_to))
    },
  })
  const actions = context.data?.allowed_actions ?? []
  const canExchange = actions.includes('exchange')
  useEffect(() => {
    if (canExchange && !attempted.current) {
      attempted.current = true
      exchange.mutate()
    }
  }, [canExchange, exchange])
  const error = authorize.error ?? confirm.error ?? exchange.error ?? context.error
  const errorCode = error instanceof ApiError ? error.errorCode : context.data?.last_error_code
  return {
    context: context.data,
    // context 已允许交换时立即显示处理中，并保持到成功跳转，避免 effect 启动前或跳转前闪出重开提示。
    loading: context.isLoading || exchange.isPending || (canExchange && !exchange.isError),
    authorizing: authorize.isPending,
    confirming: confirm.isPending,
    errorCode: errorCode ?? (error ? 'auth.wecom.service_unavailable' : null),
    canAuthorize: actions.includes('authorize_email'),
    canConfirm: actions.includes('confirm') && hasPortalSession('user'),
    canLogin: actions.includes('login'),
    account: readStoredPortalAccount('user'),
    authorize: () => authorize.mutate(),
    confirm: () => confirm.mutate(),
    goToLogin: () => {
      persistUserPostLoginReturnPath('/signin/wecom')
      window.location.assign(appendUserLoginResumeMarker('/login'))
    },
  }
}
